// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useAuth } from './useAuth';
import { useFlagOn } from './useFlagOn';
import { useShellStore, type Persona } from '../stores/shellStore';
import type { UserRole } from '../types';
import { ROLE_LABELS } from '../../shared/roles/roleLabels';

/**
 * Maps a real auth role to the shell persona that drives the Carbon UI layout
 * (the mock's operator / manager / executive views).
 *
 * - operator            → operator
 * - operations_manager  → manager
 * - admin               → manager (admin additionally keeps Users & Access;
 *                         see `isAdmin`)
 * - executive           → executive
 */
export function personaForRole(role: UserRole | null): Persona {
  switch (role) {
    case 'operator':
      return 'operator';
    case 'executive':
      return 'executive';
    case 'operations_manager':
    case 'admin':
      return 'manager';
    default:
      return 'operator';
  }
}

export interface PersonaState {
  persona: Persona;
  effectiveRole: UserRole | null;
  /** The role's display name (same map as Admin Center), or null with no role. */
  roleLabel: string | null;
  /** Admin keeps the Users & Access surfaces on top of the Manager view. */
  isAdmin: boolean;
  /** True when the persona came from a manual/dev override rather than the role. */
  isOverridden: boolean;
}

/**
 * Resolve the active persona. A manual/dev override in the shell store wins;
 * otherwise the persona is derived from the authenticated role.
 */
export function usePersona(): PersonaState {
  const { effectiveRole, isAdmin } = useAuth();
  const override = useShellStore(s => s.personaOverride);

  const switcherEnabled = useFlagOn('personaSwitcherEnabled');

  // Only admins may switch the persona view, and only while the switcher is on
  // (AB#35784); everyone else is locked to the persona their role dictates. A
  // stale persisted override is ignored either way, so turning the switcher off
  // can't leave a user on a view their permissions don't load.
  const activeOverride = isAdmin && switcherEnabled ? override : null;

  return {
    persona: activeOverride ?? personaForRole(effectiveRole),
    effectiveRole,
    roleLabel: effectiveRole ? ROLE_LABELS[effectiveRole] : null,
    isAdmin,
    isOverridden: activeOverride != null,
  };
}
